import Controller from '@ember/controller';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import type { FilePondFile } from 'filepond';
import type ScanType from 'scaleflow/models/scan';
import type RouterService from '@ember/routing/router-service';
import type StoreService from 'scaleflow/services/store';
import ScanValidations from 'scaleflow/validators/scan';
import { initializeVdrClient } from 'scaleflow/utils/aws-clients/s3VDRClient';
import {
  baseName,
  libraryKey,
  libraryPrefix,
  manualRepositoryEntry,
  scanUploadKey,
  type Repository,
} from 'scaleflow/utils/manual-repo-archives';
import type { S3ObjectSummary } from 'scaleflow/utils/aws-clients/s3VDRClient';
import {
  AI_MODELS,
  DEFAULT_AI_MODEL,
  isKnownAiModel,
  isSelectableAiModel,
} from 'scaleflow/utils/ai-models';

type VdrClient = Awaited<ReturnType<typeof initializeVdrClient>>;

type RepoStatusGroup = 'PENDING' | 'CLONING' | 'COMPLETED' | 'FAILED';
type RepoStatusFilter = 'ALL' | RepoStatusGroup;

/**
 * The same grouping the row badges use: anything not yet settled into
 * PENDING, COMPLETED or FAILED is shown as cloning.
 */
function repoStatusGroup(status: string | undefined): RepoStatusGroup {
  if (status === 'PENDING' || status === 'COMPLETED' || status === 'FAILED') {
    return status;
  }
  return 'CLONING';
}

const REPO_STATUS_FILTERS: { key: RepoStatusFilter; label: string }[] = [
  { key: 'ALL', label: 'scans.processing.repo-filter-all' },
  { key: 'PENDING', label: 'scans.processing.repo-filter-pending' },
  { key: 'CLONING', label: 'scans.processing.repo-filter-cloning' },
  { key: 'COMPLETED', label: 'scans.processing.repo-filter-ready' },
  { key: 'FAILED', label: 'scans.processing.repo-filter-failed' },
];

export default class ScansScanProcessingController extends Controller {
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare organisation: any;

  // UI-only view state for this controller's viewing session — legitimately
  // controller-scoped, since it's about which sections are expanded right
  // now rather than anything persisted about a specific scan. Reset on exit
  // by the route's resetController hook.
  @tracked isCloneBoxExpanded = false;
  @tracked isProductDiscoveryExpanded = false;
  @tracked isAddContextExpanded = false;
  @tracked isReportsUploadExpanded = false;
  @tracked contextValue: string | null = null;
  // Which half of the add-repositories control is showing. Local to this
  // viewing session, like the expansion flags above.
  @tracked activeRepoSourceTab: 'upload' | 'pick' = 'upload';
  @tracked libraryArchives: S3ObjectSummary[] = [];
  @tracked isLoadingLibrary = false;
  @tracked isAddingFromLibrary = false;
  @tracked libraryError: string | null = null;
  private hasLoadedLibrary = false;
  // True between a successful archive and the transition to the list settling.
  // See toggleArchived.
  @tracked isLeavingForList = false;
  // Which status the repos table is narrowed to. View state, like the
  // expansion flags: a long clone is easier to follow by its failures alone.
  @tracked repoStatusFilter: RepoStatusFilter = 'ALL';

  scanValidations = ScanValidations;

  declare model: ScanType;

  aiModels = AI_MODELS;

  /**
   * Falls back to the default for anything the dropdown does not offer: scans
   * created before `ai_model` existed store nothing, and scans created against
   * an earlier version of the list store a profile ID that is no longer an
   * option. Either way, leaving it unmatched would mark no `<option>` as
   * selected and the browser would display the first entry while the record
   * held something else.
   */
  get selectedAiModel(): string {
    return isKnownAiModel(this.model.ai_model)
      ? this.model.ai_model
      : DEFAULT_AI_MODEL;
  }

  get sortedRepositories() {
    return [...((this.repositories as Repository[]) ?? [])].sort((a, b) => {
      const aManual = a.source === 'manual' ? 1 : 0;
      const bManual = b.source === 'manual' ? 1 : 0;
      if (aManual !== bManual) return aManual - bManual;
      return a.full_name.localeCompare(b.full_name);
    });
  }

  /** The filter chips, each with how many repos it would show. */
  get repoStatusFilters() {
    const counts = new Map<RepoStatusFilter, number>([['ALL', 0]]);
    for (const repo of (this.repositories as Repository[]) ?? []) {
      const group = repoStatusGroup(repo.status);
      counts.set('ALL', counts.get('ALL')! + 1);
      counts.set(group, (counts.get(group) ?? 0) + 1);
    }
    return REPO_STATUS_FILTERS.map((filter) => ({
      ...filter,
      count: counts.get(filter.key) ?? 0,
      isActive: filter.key === this.repoStatusFilter,
    }));
  }

  get visibleRepositories() {
    if (this.repoStatusFilter === 'ALL') return this.sortedRepositories;
    return this.sortedRepositories.filter(
      (repo) => repoStatusGroup(repo.status) === this.repoStatusFilter,
    );
  }

  @action
  filterRepositoriesBy(filter: RepoStatusFilter) {
    this.repoStatusFilter = filter;
  }

  get completedRepositoryCount() {
    return (
      (this.repositories as Repository[] | undefined)?.filter(
        (r) => r.status === 'COMPLETED',
      ).length ?? 0
    );
  }

  get cloneJobStatus() {
    const repos = this.repositories as Repository[] | undefined;
    if (repos?.length && repos.every((r) => r.status === 'COMPLETED')) {
      return 'COMPLETED';
    }
    return this.model.clone_job_status ?? 'PENDING';
  }

  get repositories() {
    return this.model.repositories;
  }

  @action
  toggleCloneBox() {
    this.isCloneBoxExpanded = !this.isCloneBoxExpanded;
  }

  @action
  toggleProductDiscovery() {
    this.isProductDiscoveryExpanded = !this.isProductDiscoveryExpanded;
  }

  @action
  toggleAddContext() {
    this.isAddContextExpanded = !this.isAddContextExpanded;
  }

  @action
  toggleReportsUpload() {
    this.isReportsUploadExpanded = !this.isReportsUploadExpanded;
  }

  /** Repo names already on this scan, so the picker can't offer them twice. */
  get manualRepositoryNames(): string[] {
    return ((this.repositories as Repository[]) ?? []).map((r) => r.full_name);
  }

  @action
  showUploadTab() {
    this.activeRepoSourceTab = 'upload';
  }

  /**
   * The library is listed on first view of this tab rather than on page load:
   * most visits to the processing page never open it, and an extra S3 call on
   * every scan page for a control nobody touched is waste.
   */
  @action
  async showPickTab() {
    this.activeRepoSourceTab = 'pick';
    if (this.hasLoadedLibrary || this.isLoadingLibrary) return;
    await this.loadLibraryArchives();
  }

  @action
  async loadLibraryArchives() {
    this.isLoadingLibrary = true;
    this.libraryError = null;
    try {
      const client = await this.getVdrClient();
      this.libraryArchives = await client.listObjects(
        libraryPrefix(this.organisation.id as string),
      );
      this.hasLoadedLibrary = true;
    } catch (error) {
      console.error('Failed to list uploaded source code', error);
      this.libraryError = 'load-failed';
    } finally {
      this.isLoadingLibrary = false;
    }
  }

  /**
   * Adds library archives to this scan.
   *
   * Each one is copied server-side into this scan's own upload prefix before
   * its entry is committed, so `repository_url` points where the pipeline
   * already reads and the library object is never what a scan depends on.
   * Deliberately mirrors `uploadOneRepository` — same commit queue, same
   * PENDING/FAILED split — so the two read as siblings.
   */
  @action
  async addRepositoriesFromLibrary(filenames: string[]) {
    const scan = this.model;
    if (!filenames.length || this.isAddingFromLibrary) return;

    this.isAddingFromLibrary = true;
    const tenantId = this.organisation.id as string;

    try {
      const client = await this.getVdrClient();
      await Promise.all(
        filenames.map(async (filename) => {
          const entry = manualRepositoryEntry({
            bucket: client.vdrBucket,
            tenantId,
            scanId: scan.id as string,
            filename,
          });

          try {
            await client.copyObject(
              libraryKey(tenantId, filename),
              scanUploadKey(tenantId, scan.id as string, filename),
            );
            await this.commitRepositoryEntry(scan, {
              ...entry,
              status: 'PENDING',
            });
          } catch (error) {
            console.error(
              `Failed to add "${filename}" from the library`,
              error,
            );
            await this.commitRepositoryEntry(scan, {
              ...entry,
              status: 'FAILED',
            });
          }
        }),
      );
    } finally {
      this.isAddingFromLibrary = false;
    }
  }

  @action
  updateContext(event: Event) {
    this.contextValue = (event.target as HTMLTextAreaElement).value;
  }

  /**
   * `this.model` is captured into `scan` up front in every action below and
   * used exclusively from there on. This controller is an Ember singleton
   * reused across scans as the user navigates — if an action re-read
   * `this.model` after an `await`, a save started for one scan could resolve
   * against whatever scan the controller has since been reused for. Busy
   * flags live on the scan record for the same reason: see
   * docs/ai-blueprints/scan-archiving.md.
   */
  @action
  async markAsCompleted() {
    const scan = this.model;
    if (scan.isMarkingCompleted) return;
    scan.isMarkingCompleted = true;

    try {
      scan.status = 'COMPLETED';
      await scan.save();
    } finally {
      scan.isMarkingCompleted = false;
    }
  }

  /**
   * Calling a run off by hand. `ABORTED` is terminal (see `isTerminal` on the
   * model), so this hides every action control on the page including itself —
   * the same one-way door `markAsCompleted` walks through.
   */
  @action
  async markAsAborted() {
    const scan = this.model;
    if (scan.isMarkingAborted) return;
    scan.isMarkingAborted = true;

    try {
      scan.status = 'ABORTED';
      await scan.save();
    } finally {
      scan.isMarkingAborted = false;
    }
  }

  @action
  async saveContext() {
    const scan = this.model;
    const contextValue = this.contextValue ?? 'No Context';
    scan.isSavingContext = true;

    try {
      scan.context = contextValue;
      scan.context_injection_status = 'INITIATE';
      await scan.save();
    } finally {
      scan.isSavingContext = false;
    }
  }

  /**
   * `Atoms::Forms::Select` calls `@handleChange` with the changeset and field id
   * ahead of the event, so the event is the third argument. Passing
   * `@handleChange` at all is required here: without it — and without a
   * changeset — the component's own handler would call `set` on `undefined`.
   */
  @action
  async updateAiModel(_changeset: unknown, _field: unknown, event: Event) {
    const scan = this.model;
    const value = (event.target as HTMLSelectElement).value;
    if (!value || value === scan.ai_model || scan.isSavingAiModel) return;

    // The select is already disabled while a step runs, but a step can start
    // between render and this event (a poll landing while the native dropdown
    // is open), so enforce the rule here too rather than relying on the
    // disabled attribute alone.
    if (scan.hasRunningStep) return;

    // Same reasoning for the per-option restriction: the browser will not let a
    // disabled `<option>` be chosen, but that is a DOM affordance rather than a
    // rule, and this is the only place the value reaches the record.
    if (!isSelectableAiModel(value)) return;

    const previous = scan.ai_model;
    scan.isSavingAiModel = true;
    try {
      scan.ai_model = value;
      await scan.save();
    } catch {
      // Put the record back so the select keeps matching what is persisted.
      scan.ai_model = previous;
    } finally {
      scan.isSavingAiModel = false;
    }
  }

  /**
   * One button for both directions — the Danger zone shows Archive or
   * Un-archive depending on where the scan currently is.
   */
  @action
  async toggleArchived() {
    const scan = this.model;
    if (scan.isArchiving || this.isLeavingForList) return;

    const archived = !scan.archived;
    scan.isArchiving = true;

    try {
      scan.archived = archived;
      await scan.save();
    } catch {
      // Put the record back so the button keeps reflecting what is persisted,
      // and can be retried.
      scan.archived = !archived;
      return;
    } finally {
      // Always cleared, even on the archive path that navigates away. This flag
      // means only "a save touching this scan is in flight"; leaving it set rode
      // along on the record into the list, where it disabled that scan's row
      // button on the tab it had just moved to.
      scan.isArchiving = false;
    }

    if (archived) {
      // The scan has just left the active list, so send the user there to see it
      // gone. `isLeavingForList` keeps the button dead while the transition
      // runs — without it a second click would compute `!archived` and undo the
      // archive that just succeeded. Controller-local rather than on the record
      // because it is about this visit, not about the scan.
      this.isLeavingForList = true;
      // Self-healing: on success the template is torn down, so clearing is moot;
      // if the transition is aborted the user is still here and the button has to
      // work again. `then(release, release)` rather than `finally` — an aborted
      // Transition rejects, and `finally` would re-throw that as an unhandled
      // rejection.
      const release = () => (this.isLeavingForList = false);
      this.router.transitionTo('scans.list').then(release, release);
      return;
    }

    // Un-archiving keeps the user here. The reason to un-archive is to carry on
    // with the scan, and the button flipping back to Archive is feedback enough.
  }

  @action
  async refreshReportsCredentials() {
    const scan = this.model;
    if (scan.isRefreshingCredentials) return;
    scan.isRefreshingCredentials = true;

    try {
      scan.reports_url_generation_status = 'INITIATE';
      await scan.save();
    } finally {
      scan.isRefreshingCredentials = false;
    }
  }

  @action
  async publishReports() {
    const scan = this.model;
    scan.vdr_delivery_and_publish_status = 'INITIATE';
    await scan.save();
  }

  @action
  async retryRepository(repo: Repository) {
    const scan = this.model;
    if (scan.isSaving) return;

    const retried: Repository[] = [];
    scan.repositories = (scan.repositories as Repository[]).map((r) => {
      if (r.full_name !== repo.full_name) return r;
      const pending = { ...r, status: 'PENDING' };
      retried.push(pending);
      return pending;
    });

    await scan.save({ adapterOptions: { repositories: retried } });
  }

  /**
   * Entries waiting for the next save, keyed by `full_name` so a re-drop of
   * the same file only sends its latest status. Uploads run in parallel, but
   * only one save is ever in flight: entries that finish while it runs wait
   * here and go out together in the next one, like data-socket's message
   * queue. A drop of N archives is a handful of PATCHes rather than N.
   */
  private pendingRepositoryEntries = new Map<string, Repository>();
  private repositoryFlush: Promise<void> | null = null;

  private commitRepositoryEntry(scan: ScanType, entry: Repository) {
    this.pendingRepositoryEntries.set(entry.full_name, entry);
    this.repositoryFlush ??= this.flushRepositoryEntries(scan);
    return this.repositoryFlush;
  }

  private async flushRepositoryEntries(scan: ScanType) {
    while (this.pendingRepositoryEntries.size > 0) {
      const batch = [...this.pendingRepositoryEntries.values()];
      this.pendingRepositoryEntries.clear();

      const names = new Set(batch.map((r) => r.full_name));
      const existing = (scan.repositories as Repository[]) ?? [];
      scan.repositories = [
        ...existing.filter((r) => !names.has(r.full_name)),
        ...batch,
      ];
      // The uploads have already confirmed/failed in VDR by this point, so a
      // save failure here — after the scan adapter's own retries — leaves the
      // scan record out of sync with what VDR actually holds (a known v1 gap;
      // there's no reconciliation job).
      try {
        await scan.save({ adapterOptions: { repositories: batch } });
      } catch (error) {
        for (const entry of batch) {
          console.error(
            `Failed to persist ${entry.status} status for uploaded repository "${entry.full_name}"`,
            error,
          );
        }
      }
    }
    this.repositoryFlush = null;
  }

  @action
  async uploadRepositories(pondFiles: FilePondFile[]) {
    const scan = this.model;
    if (!pondFiles.length) return;

    // Last-drop-wins by base name, matching the accepted "silently overwrite"
    // collision rule, within this dropped batch.
    const dropped = new Map<string, FilePondFile>();
    for (const pondFile of pondFiles) {
      dropped.set(baseName((pondFile.file as File).name), pondFile);
    }

    const client = await this.getVdrClient();
    await Promise.all(
      Array.from(dropped.values()).map((pondFile) =>
        this.uploadOneRepository(scan, pondFile.file as File, client),
      ),
    );
  }

  /** Extracted so tests can override it on the instance rather than hit AWS. */
  private async getVdrClient(): Promise<VdrClient> {
    return initializeVdrClient(this.store);
  }

  /**
   * A repo only becomes part of `scan.repositories` — and so only shows up in
   * the table and gets PATCHed to the backend — once its own upload actually
   * resolves. Nothing is registered up front: a batch of N drops must not
   * PATCH all N before any of them have finished uploading.
   *
   * A failed upload can't be retried from the row — the file object isn't
   * persisted anywhere to retry from after a reload. The user re-drops a file
   * with the same name, which re-uploads and re-commits via this same path.
   * The row's retry button only re-queues an archive already in VDR.
   */
  private async uploadOneRepository(
    scan: ScanType,
    file: File,
    client: VdrClient,
  ) {
    const key = scanUploadKey(
      this.organisation.id as string,
      scan.id as string,
      file.name,
    );
    const entry = manualRepositoryEntry({
      bucket: client.vdrBucket,
      tenantId: this.organisation.id as string,
      scanId: scan.id as string,
      filename: file.name,
    });

    try {
      await client.uploadFileMultipart({
        key,
        body: file,
        contentType: file.type || 'application/gzip',
      });
      // Stays PENDING, not COMPLETED — the file landing in VDR is what lets
      // the backend queue it for a background worker to copy into the
      // workspace; the backend, not the frontend, marks it COMPLETED once
      // that copy actually happens (mirrors how a GitHub clone job's own
      // callback — not the frontend — flips its repo to COMPLETED).
      await this.commitRepositoryEntry(scan, { ...entry, status: 'PENDING' });
    } catch {
      await this.commitRepositoryEntry(scan, { ...entry, status: 'FAILED' });
    }
  }

  @action
  async startProductDiscovery() {
    const scan = this.model;
    scan.clone_job_status = 'COMPLETED';
    scan.product_discovery_status = 'INITIATE';
    await scan.save();
  }

  @action
  async retryProductDiscovery() {
    const scan = this.model;
    if (scan.isSaving) return;

    scan.product_discovery_status = 'INITIATE';
    await scan.save();
  }

  @action
  async retryContextInjection() {
    const scan = this.model;
    if (scan.isSavingContext) return;
    scan.isSavingContext = true;

    try {
      scan.context_injection_status = 'PENDING';
      await scan.save();
    } finally {
      scan.isSavingContext = false;
    }
  }

  @action
  async startAnalysis() {
    const scan = this.model;
    // TODO: trigger analysis
    scan.analysis_job_status = 'INITIATE';
    await scan.save();
  }

  @action
  async retryAnalysis() {
    const scan = this.model;
    if (scan.isSaving) return;

    scan.analysis_job_status = 'INITIATE';
    await scan.save();
  }

  @action
  async retryAll() {
    const scan = this.model;
    if (scan.isSaving) return;

    const retried: Repository[] = [];
    scan.repositories = (scan.repositories as Repository[]).map((r) => {
      const pending = { ...r, status: 'PENDING' };
      retried.push(pending);
      return pending;
    });

    await scan.save({ adapterOptions: { repositories: retried } });
  }

  @action
  async submitProductConfigReview(config: string) {
    const scan = this.model;
    scan.product_config = config;
    scan.product_discovery_status = 'REVIEWED';
    await scan.save();
  }

  @action
  async uploadReports(pondFiles: FilePondFile[]) {
    const scan = this.model;
    const credentials = JSON.parse(scan.publish_reports_url_credentials) as {
      url: string;
      fields: Record<string, string>;
    };
    const { url, fields } = credentials;

    await Promise.all(
      pondFiles.map((pondFile) => {
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) =>
          formData.append(key, value),
        );
        formData.append('file', pondFile.file as Blob);
        return fetch(url, { method: 'POST', body: formData });
      }),
    );
  }
}
